import { isGoogleRenderer } from '../utils/botDetection'
import { decideReload } from '../utils/reloadGuard'

/**
 * Self-heals clients running a stale build after a deploy.
 *
 * Chunks are content-hashed and Netlify deploys are atomic, so a client
 * holding old HTML/JS 404s on any chunk it lazy-loads after a release. The
 * built-in `chunk-reload.client` plugin only recovers chunk errors thrown
 * during a route navigation; a chunk that fails outside a navigation (lazy
 * component on interaction or scroll) leaves the page silently broken. This
 * plugin reloads on ANY chunk error, and preemptively refreshes hidden tabs
 * as soon as the app manifest reports a new build.
 *
 * Reload loops are prevented by an escalating per-path block window (see
 * `utils/reloadGuard.ts`) persisted in `window.name` rather than
 * sessionStorage: RUM shows real sessions where storage is full
 * (QuotaExceededError on every setItem), where `reloadNuxtApp`'s own
 * sessionStorage guard fails silently and would reload without protection.
 *
 * `persistState` is deliberately not used: `experimental.restoreState` is
 * off, so the state blob would never be read back — it would only sit in the
 * already quota-stressed sessionStorage.
 *
 * Google's renderers are exempt from the chunk-error reload: the reload exists
 * to rescue a human session, and on a crawler it only burns rendering budget
 * that was already too short to fetch the JS in the first place.
 *
 * The checkout exemption matches on the URL path. A stricter alternative is
 * matching the route name (see `isCheckoutPage` in `app.vue`), e.g.:
 *
 *   if (router.currentRoute.value.name === 'localized-checkout') return
 *
 * which cannot false-positive on unrelated URLs containing '/checkout', at
 * the cost of breaking silently if the page file is ever renamed.
 */
export default defineNuxtPlugin({
  name: 'armani:stale-build-reload',
  setup(nuxtApp) {
    const router = useRouter()

    const guardedReload = (path: string) => {
      const { allowed, guard } = decideReload(window.name, path, Date.now())
      if (!allowed) {
        // console.error survives the build and reaches Datadog RUM
        console.error(
          `[stale-build-reload] reload of ${path} suppressed to avoid a reload loop`
        )
        return
      }
      window.name = guard
      // force: true skips reloadNuxtApp's own 10s guard — ours is stricter
      reloadNuxtApp({ path, force: true })
    }

    let navigatingTo: string | null = null
    addRouteMiddleware(to => {
      navigatingTo = to.fullPath
    })
    router.afterEach(() => {
      navigatingTo = null
    })

    // Chunk error: reload the app to fetch the new chunks and refresh the content.
    // if cross-navigation: recovers the error by reloading the next page
    // if same page: reloads the page keeping the query params
    nuxtApp.hook('app:chunkError', ({ error }) => {
      // failing to preload CSS is not fatal (styles are inlined in the document)
      if ((error?.message ?? '').includes('Unable to preload CSS')) return
      // never wipe an in-progress checkout: form state does not survive
      if (router.currentRoute.value.path.includes('/checkout')) return
      // Google's renderers get no reload. Search Console fails these pages on an
      // exhausted rendering budget - it cannot fetch all the JS - and a chunk it
      // failed to fetch is exactly what raises this hook. Reloading restarts the
      // render from zero and spends more of the budget that was already short.
      // There is no human session here to rescue, so the reload only ever costs.
      if (isGoogleRenderer(navigator.userAgent)) return
      guardedReload(navigatingTo ?? router.currentRoute.value.path)
    })

    let watchingVisibility = false
    // New deploy detected: reload the app once the user is not looking at the tab
    nuxtApp.hook('app:manifest:update', () => {
      // only reload when document is minimized or in background
      if (document.visibilityState === 'hidden') {
        // never wipe an in-progress checkout: form state does not survive
        if (router.currentRoute.value.path.includes('/checkout')) return
        guardedReload(router.currentRoute.value.path)
        return
      }

      // if the document is being viewed when a reload is needed, reload the next time it's not viewed
      if (!watchingVisibility) {
        watchingVisibility = true
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'hidden') return
          if (router.currentRoute.value.path.includes('/checkout')) return
          guardedReload(router.currentRoute.value.path)
        })
      }
    })
  },
})
